/**
 * Public and internal types for `@contextune/sdk`.
 */

// ---------------------------------------------------------------------------
// Shared output types (used by both internal engine files and public surface)
// ---------------------------------------------------------------------------

/** Frozen-at-init marketing context. */
export interface MarketingParamsBlock {
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_term: string | null;
  utm_content: string | null;
  referrer: string | null;
  landing_page: string | null;
}

/** Static device information, read at snapshot time from `navigator` + `window`. */
export interface DeviceInfoBlock {
  user_agent: string;
  viewport_w: number;
  viewport_h: number;
  language: string;
  platform: string;
  device_type: 'desktop' | 'tablet' | 'mobile';
}

/**
 * A single entry in the event log.
 *
 * `name` is the normalised event name (e.g. `'page_view'`, `'track'`).
 * `elapsed_s` is whole seconds since session start.
 */
export interface EventLogEntry {
  name: string;
  elapsed_s: number;
  properties?: Record<string, unknown>;
}

// ---------------------------------------------------------------------------
// Public snapshot type
// ---------------------------------------------------------------------------

export interface ContextuneSnapshot {
  event_log: EventLogEntry[];
  marketing_params?: MarketingParamsBlock;
  device_info?: DeviceInfoBlock;
}

export type SnapshotFormat = 'js' | 'json' | 'toon';

// ---------------------------------------------------------------------------
// Internal network-intercept types (not re-exported from index.ts)
// ---------------------------------------------------------------------------

export const PROVIDER_REGEX = {
  "google-analytics.com": "googleanalytics",
  "analytics.google.com": "googleanalytics",
  "api.segment.io/v1/t": "segment",
  "segmentapi": "segment",
  "seg-api": "segment",
  "segment-api": "segment",
  "/.*api(-eu)?(-js)?.mixpanel.com.*/": "mixpanel",
} as const;

export type Providers = (typeof PROVIDER_REGEX)[keyof typeof PROVIDER_REGEX];

export type CapturedEvent = {
  endpoint: string;
  method: "POST" | "GET";
  payload?: string | null;
  transport: "XHR" | "beacon" | "fetch";
  provider: Providers;
};

export type CapturedEventFromCallback = CapturedEvent & {
  href: string;
  sentAt: Date;
};

export type EventsExtractedFromBatch = {
  eventName: string;
  body: string | null;
  href: string;
  method: string;
  transport: string;
  provider: Providers;
  sentAt: Date;
  properties?: Record<string, unknown>;
}[];

export type AddEventCallback = (event: CapturedEvent) => void;
